import { AfterViewInit, Component, ElementRef, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl } from '@ngneat/reactive-forms';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Role } from 'src/app/classes/auth/role.class';
import { Leave } from 'src/app/classes/leave';
import { PlanningHasEntity } from 'src/app/classes/planning-has-entity.class';
import { Planning } from 'src/app/classes/planning.class';
import { Project } from 'src/app/classes/project.class';
import { UserPlanning } from 'src/app/classes/user-planning';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { EmployeeDayInfoDialogComponent } from 'src/app/pages/employee-planning/employee-day-info-dialog/employee-day-info-dialog.component';
import { ACTIVITIES } from 'src/app/pages/project-executor-planning/activity-constants';
import { HourtypeIconPipe } from 'src/app/pipes/hourtype-icon.pipe';
import { AuthService } from 'src/app/services/auth/auth.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { EmployeeService } from 'src/app/services/employee.service';
import { EntityType } from 'src/app/services/entities/entity-type.class';
import { LeaveService } from 'src/app/services/leave.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { UserPlanningService } from 'src/app/services/planning/user-planning.service';
import { ProjectService } from 'src/app/services/project.service';
import { StaffingDisplayMode, StaffingSettings, USER_SETTINGS_MODULE_KEYS } from 'src/app/services/user/user-settings';
import { UserService } from 'src/app/services/user/user.service';
import { Utils } from 'src/app/utils.class';
import { entityTypes } from 'src/environments/trackline/entitytypes';

interface PlanningOverviewDay {
    date: Date;
    dateKey: string;
    dayLabel: string;
    dayNumber: number;
    isWeekend: boolean;
}

interface PlanningOverviewWeek {
    key: string;
    weekNumber: number;
    rangeLabel: string;
    days: PlanningOverviewDay[];
}

interface PlanningOverviewCell {
    title: string;
    leaveIconName?: string;
    entityTypeIcon?: string;
    projectIcon?: string;
    extraPlanningCount: number;
    isNight: boolean;
}

@Component({
    selector: 'app-staffing',
    templateUrl: './staffing.component.html',
    styleUrls: ['./staffing.component.scss'],
    providers: [HourtypeIconPipe],
    standalone: false,
})
export class StaffingComponent extends CodaltComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('planningGrid') planningGridRef?: ElementRef<HTMLDivElement>;

    public users: User[] = [];
    public uniqueRoles: Array<Role & { users: User[] }> = [];
    public projects: Project[] = [];
    public plannings: Planning[] = [];
    public userPlannings: UserPlanning[] = [];
    public leaves: Leave[] = [];
    public planningByUserAndDate: Record<number, Record<string, PlanningOverviewCell>> = {};
    public contextUser?: User;
    public contextDay?: Date;

    public readonly StaffingDisplayMode = StaffingDisplayMode;
    public displayModeControl = new FormControl<StaffingDisplayMode>(StaffingDisplayMode.All);

    private activityIconMap = new Map(ACTIVITIES.map(activity => [activity.value, activity.icon]));
    private planningById = new Map<number, Planning>();
    private entityTypesByKey = this.createEntityTypesByKey();
    private employeeService = inject(EmployeeService);
    private authService = inject(AuthService);
    private authorisationService = inject(AuthorisationService);
    private leaveService = inject(LeaveService);
    private planningService = inject(PlanningService);
    private projectService = inject(ProjectService);
    private userService = inject(UserService);
    private userPlanningService = inject(UserPlanningService);
    private dialog = inject(MatDialog);
    private ngZone = inject(NgZone);
    private cleanupPlanningPointerOver?: () => void;
    private highlightedCells: HTMLElement[] = [];
    private highlightedKey?: string;
    private hourtypeIconPipe = inject(HourtypeIconPipe);
    private leaveByUserDay = new Map<string, Leave>();
    private staffingSettings: StaffingSettings = {};

    /** Sets the browser title and loads all source data for the overview period. */
    ngOnInit() {
        const fromDate = this.days[0]?.date ?? new Date();
        const toDate = new Date(this.days[this.days.length - 1]?.date ?? new Date());
        toDate.setDate(toDate.getDate() + 1);

        this.subscriptions.add(
            this.authService.getUserDetails().subscribe(response => {
                this.staffingSettings = response.settings?.modules?.[USER_SETTINGS_MODULE_KEYS.STAFFING] ?? {
                    displayMode: StaffingDisplayMode.All,
                };
                this.displayModeControl.setValue(this.staffingSettings.displayMode, { emitEvent: false });
                this.planningByUserAndDate = this.createPlanningByUserAndDate();
            })
        );

        this.subscriptions.add(
            this.displayModeControl.valueChanges.subscribe(displayMode => {
                this.planningByUserAndDate = this.createPlanningByUserAndDate();
                this.userService
                    .saveUserSettings(USER_SETTINGS_MODULE_KEYS.STAFFING, {
                        ...this.staffingSettings,
                        displayMode: displayMode ?? StaffingDisplayMode.All,
                    })
                    .subscribe(response => {
                        this.staffingSettings = response.data.modules?.[USER_SETTINGS_MODULE_KEYS.STAFFING] ?? {};
                    });
            })
        );

        this.subscriptions.add(
            combineLatest([
                this.userService
                    .getFilteredList(fromDate, toDate, false, false)
                    .pipe(
                        map(users =>
                            users.sort((a, b) =>
                                (a.lastname ?? a.name ?? '').localeCompare(b.lastname ?? b.name ?? '', 'nl')
                            )
                        )
                    ),
                this.projectService.getFilteredList(fromDate, toDate),
                this.planningService.getFilteredList(fromDate, toDate),
                this.userPlanningService.getFilteredList(fromDate, toDate),
                this.leaveService.getFilteredList(fromDate, toDate),
                this.authorisationService.getRolesMap(),
            ]).subscribe(([users, projects, plannings, userPlannings, leaves, rolesMap]) => {
                plannings
                    .filter(planning => !!planning?.id)
                    .forEach(planning => {
                        if (!planning.mainPlanning) {
                            const mainPlanning = Utils.mainPlanning(planning);
                            if (mainPlanning) {
                                planning.mainPlanning = mainPlanning;
                            }
                        }
                    });

                this.planningById = new Map(
                    plannings
                        .filter((planning): planning is Planning & { id: number } => !!planning?.id)
                        .map(planning => [planning.id, planning])
                );

                this.projects = projects;
                this.plannings = plannings;
                this.userPlannings = userPlannings;
                this.users = users.filter(user => !!user.id);
                this.leaves = leaves;
                const seenUserIds = new Set<number>();
                this.uniqueRoles = Array.from(rolesMap.values())
                    .filter(role => role.functionalities?.some(f => f.key === Functionalities.Plannable) ?? false)
                    .map(role => {
                        const roleUsers = this.users.filter(
                            user => user.roles?.some(userRole => userRole.key === role.key) && !seenUserIds.has(user.id)
                        );
                        roleUsers.forEach(user => seenUserIds.add(user.id ?? 0));

                        return { ...role, users: roleUsers };
                    })
                    .filter(role => role.users.length);
                this.planningByUserAndDate = this.createPlanningByUserAndDate();
            })
        );
    }

    /** Starts pointer-based hover highlighting once the grid exists in the DOM. */
    ngAfterViewInit(): void {
        this.setupPlanningPointerOverListener();
    }

    /** Clears DOM hover state and removes event listeners when the component is destroyed. */
    ngOnDestroy(): void {
        this.clearHighlightedCells();
        this.cleanupPlanningPointerOver?.();
        super.ngOnDestroy();
    }

    weeks: PlanningOverviewWeek[] = this.createWeeks();

    days = this.weeks.flatMap(week => week.days);

    /** Builds the 20-week window shown in the overview, including precomputed day labels. */
    private createWeeks() {
        const currentWeekStart = Utils.getMonday(new Date());
        currentWeekStart.setHours(0, 0, 0, 0);

        return Array.from({ length: 20 }, (_, weekOffset) => {
            const weekStart = new Date(currentWeekStart);
            weekStart.setDate(currentWeekStart.getDate() + weekOffset * 7);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);

            return {
                key: Utils.formatDate(weekStart),
                weekNumber: Utils.getWeek(weekStart),
                rangeLabel: `${Utils.formatDate(weekStart, 'EEE d MMM')} - ${Utils.formatDate(weekEnd, 'EEE d MMM')}`,
                days: Array.from({ length: 7 }, (_, dayOffset) => {
                    const date = new Date(weekStart);
                    date.setDate(weekStart.getDate() + dayOffset);

                    return {
                        date,
                        dateKey: Utils.formatDate(date),
                        dayLabel: Utils.formatDate(date, 'EEE'),
                        dayNumber: date.getDate(),
                        isWeekend: !Utils.isWorkingday(date),
                    };
                }),
            };
        });
    }

    /** Maps raw leave and planning data to compact overview cells keyed by user and day. */
    private createPlanningByUserAndDate() {
        type CellSource = {
            leave?: Leave;
            userPlannings: UserPlanning[];
            planningHas: PlanningHasEntity[];
        };

        const projectMap = new Map(this.projects.map(project => [project.afas_project_id, project]));
        const planningProjectIdMap = new Map(
            this.plannings
                .filter(planning => planning.id)
                .map(planning => [planning.id, planning.afas_project_id ?? planning.worknumber])
        );
        const leaveMap = new Map<string, Leave>();
        const cellSourceMap = new Map<string, CellSource>();
        const result: Record<number, Record<string, PlanningOverviewCell>> = {};

        const ensureCellSource = (key: string): CellSource => {
            const source = cellSourceMap.get(key);

            if (source) {
                return source;
            }

            const newSource: CellSource = {
                userPlannings: [] as UserPlanning[],
                planningHas: [] as PlanningHasEntity[],
            };
            cellSourceMap.set(key, newSource);
            return newSource;
        };

        // Lees de planning liefst van de entry zelf, anders uit de reeds opgebouwde lookup.
        const getPlanning = (planningId?: number, planning?: Planning) => {
            return planning ?? (planningId ? this.planningById.get(planningId) : undefined);
        };

        // Houd de tooltip simpel: alleen verloftekst en projectnamen uit de brondata.
        const getLeaveTitle = (leave: Leave) => {
            const title = leave.comment?.trim() || 'Verlof';
            return leave.afas_project_id ? `${title} - ${leave.afas_project_id}` : title;
        };

        // Gebruik de projectgroep alleen als laatste fallback voor het werkicoon.
        const getProjectActivityKey = (project?: Project) => {
            const activityKey = `${project?.group ?? ''}`.trim().toLowerCase();
            return this.activityIconMap.has(activityKey) ? activityKey : 'project';
        };

        // Maak per cel een unieke projectlijst op basis van user plannings en planning_has.
        const getProjectsForCell = (userPlanningsForDay: UserPlanning[], planningHasForDay: PlanningHasEntity[]) => {
            const projectEntriesMap = new Map<string, Project>();
            const addProject = (projectId?: string) => {
                if (!projectId || projectEntriesMap.has(projectId)) {
                    return;
                }

                const project = projectMap.get(projectId);
                if (project) {
                    projectEntriesMap.set(projectId, project);
                }
            };

            userPlanningsForDay.forEach(userPlanning => {
                const planning = getPlanning(userPlanning.planning_id, userPlanning.planning);
                addProject(
                    userPlanning.afas_project_id ??
                        planning?.afas_project_id ??
                        planning?.worknumber ??
                        planningProjectIdMap.get(userPlanning.planning_id)
                );
            });

            planningHasForDay.forEach(planningHas => {
                const planning = getPlanning(planningHas.planning_id, planningHas.planning);
                addProject(
                    planningHas.afas_project_id ??
                        planning?.afas_project_id ??
                        planning?.worknumber ??
                        planningProjectIdMap.get(planningHas.planning_id)
                );
            });

            return Array.from(projectEntriesMap.values());
        };

        // Haal een entitytype-icoon op uit de planning zelf, of via het bovenliggende entitytype.
        const getPlanningEntityTypeIcon = (planning?: Planning) => {
            const entityTypeKey = planning?.mainPlanning?.entitytype_key ?? planning?.planning_has?.[0]?.entitytype_key;

            if (!entityTypeKey) {
                return undefined;
            }

            const directEntityType = this.entityTypesByKey.get(entityTypeKey);
            if (!directEntityType) {
                return undefined;
            }

            if (directEntityType.icon) {
                return directEntityType.icon;
            }

            return directEntityType.belongs_to
                ? this.entityTypesByKey.get(directEntityType.belongs_to)?.icon
                : undefined;
        };

        // Vertaal relevante planningtypes naar de activity icon set van deze pagina.
        const getActivityForEntityType = (entityTypeKey: string) => {
            switch (entityTypeKey) {
                case 'asfaltTeam':
                    return 'asfalt';
                case 'cutter':
                    return 'frezen';
                case 'preparationTeam':
                    return 'voorbereiding';
                case 'projectTeam':
                    return 'project';
                default:
                    return undefined;
            }
        };

        // Kies één activity key uit planningdata, met projectgroep als laatste fallback.
        const getPlanningActivityKey = (planning?: Planning) => {
            const mainActivity = planning?.mainPlanning?.entitytype_key
                ? getActivityForEntityType(planning.mainPlanning.entitytype_key)
                : undefined;
            if (mainActivity && this.activityIconMap.has(mainActivity)) {
                return mainActivity;
            }

            const fallbackActivity = planning?.planning_has?.[0]?.entitytype_key
                ? getActivityForEntityType(planning.planning_has[0].entitytype_key)
                : undefined;
            return fallbackActivity && this.activityIconMap.has(fallbackActivity) ? fallbackActivity : undefined;
        };

        this.leaves
            .filter(leave => !leave.deleted_at)
            .forEach(leave => this.employeeService.leaveToMap(leaveMap, leave, this.users));
        this.leaveByUserDay = leaveMap;

        leaveMap.forEach((leave, key) => {
            ensureCellSource(key).leave = leave;
        });

        this.userPlannings
            .filter(userPlanning => !!userPlanning.user_id && !!userPlanning.begindate && !userPlanning.deleted_at)
            .forEach(userPlanning => {
                const key = this.makeUserDayKey(new Date(userPlanning.begindate), userPlanning.user_id);
                ensureCellSource(key).userPlannings.push(userPlanning);
            });

        this.plannings.forEach(planning => {
            (planning.planning_has ?? [])
                .filter(
                    planningHas => !!planningHas.driver_user_id && !!planningHas.begindate && !planningHas.deleted_at
                )
                .forEach(planningHas => {
                    planningHas.planning = planningHas.planning ?? planning;

                    const key = this.makeUserDayKey(new Date(planningHas.begindate), planningHas.driver_user_id);
                    ensureCellSource(key).planningHas.push(planningHas);
                });
        });

        const showLeave =
            this.displayModeControl.value === StaffingDisplayMode.Leave ||
            this.displayModeControl.value === StaffingDisplayMode.All;
        const showPlanning =
            this.displayModeControl.value === StaffingDisplayMode.Planning ||
            this.displayModeControl.value === StaffingDisplayMode.All;

        cellSourceMap.forEach((source, userDayKey) => {
            const dateKey = userDayKey.slice(0, 10);
            const userId = Number(userDayKey.slice(11));
            const leave = showLeave ? source.leave : null;
            const dayUserPlannings = showPlanning ? source.userPlannings : [];
            const dayPlanningHas = showPlanning ? source.planningHas : [];
            const projectEntries = getProjectsForCell(dayUserPlannings, dayPlanningHas);
            const hasLeave = !!leave;
            const hasPlanning = dayUserPlannings.length > 0 || dayPlanningHas.length > 0;

            if (!hasLeave && !hasPlanning) {
                return;
            }

            // Pak het eerste bruikbare entitytype-icoon uit user plannings, anders uit planning_has.
            const entityTypeIcon =
                dayUserPlannings
                    .map(userPlanning =>
                        getPlanningEntityTypeIcon(getPlanning(userPlanning.planning_id, userPlanning.planning))
                    )
                    .find(icon => !!icon) ??
                dayPlanningHas
                    .map(planningHas =>
                        getPlanningEntityTypeIcon(getPlanning(planningHas.planning_id, planningHas.planning))
                    )
                    .find(icon => !!icon);

            // Toon één werkicoon per cel op basis van planningtype, met projectgroep als fallback.
            const activityKey =
                dayUserPlannings
                    .map(userPlanning =>
                        getPlanningActivityKey(getPlanning(userPlanning.planning_id, userPlanning.planning))
                    )
                    .find(key => !!key) ??
                dayPlanningHas
                    .map(planningHas =>
                        getPlanningActivityKey(getPlanning(planningHas.planning_id, planningHas.planning))
                    )
                    .find(key => !!key) ??
                getProjectActivityKey(projectEntries[0]);

            result[userId] = result[userId] ?? {};
            result[userId][dateKey] = {
                title: [leave ? getLeaveTitle(leave) : null, ...projectEntries.map(project => project.name)]
                    .filter(Boolean)
                    .join('\n'),
                leaveIconName: leave ? this.hourtypeIconPipe.transform(leave.hourtype) : undefined,
                entityTypeIcon,
                projectIcon: hasPlanning
                    ? (this.activityIconMap.get(activityKey) ?? this.activityIconMap.get('project') ?? 'more_horiz')
                    : undefined,
                extraPlanningCount: Math.max(dayUserPlannings.length + dayPlanningHas.length - 1, 0),
                isNight:
                    dayUserPlannings.some(up => Utils.isNight(up.begindate, up.enddate)) ||
                    dayPlanningHas.some(phe => Utils.isNight(phe.begindate, phe.enddate)),
            };
        });

        return result;
    }

    /** Builds a quick lookup by entity-type key for icon and belongs-to resolution. */
    private createEntityTypesByKey() {
        const map = new Map<string, EntityType>();

        Object.values(entityTypes).forEach(entityType => {
            if (entityType.key) {
                map.set(entityType.key, entityType);
            }
        });

        return map;
    }

    /** Creates the shared user-day key used for overview source maps. */
    private makeUserDayKey(date: Date, userId: number) {
        return `${Utils.formatDate(date)}-${userId}`;
    }

    /** Stores the selected user/day so the context menu can open the matching detail dialog. */
    onCellContextMenu(user: User, day: PlanningOverviewDay) {
        this.contextUser = user;
        this.contextDay = day.date;
    }

    /** Opens the employee day detail dialog using the original planning and leave sources. */
    showInfoDialog() {
        if (!this.contextUser?.id || !this.contextDay) {
            return;
        }

        const dayKey = Utils.formatDate(this.contextDay);
        const planningHas = this.getPlanningHasForDay(this.contextUser.id, dayKey);
        const userPlanning = this.getUserPlanningsForDay(this.contextUser.id, dayKey);
        const leave = this.getLeaveForDay(this.contextUser.id, this.contextDay);

        this.dialog.open(EmployeeDayInfoDialogComponent, {
            minWidth: '320px',
            panelClass: 'comment-edit-dialog',
            data: {
                planningHas,
                userPlanning,
                leaves: leave ? [leave] : [],
                afasLeave: undefined,
                projects: this.projects,
                planningList: this.plannings,
                user: this.contextUser,
                day: this.contextDay,
                showLinks: true,
            },
        });
    }

    /** Returns the leave for a user on a specific day from the lightweight leave map. */
    private getLeaveForDay(userId: number, day: Date) {
        return this.leaveByUserDay.get(this.makeUserDayKey(day, userId));
    }

    /** Collects all planning-has entries for one user on one day for the detail dialog. */
    private getPlanningHasForDay(userId: number, dayKey: string) {
        const result: PlanningHasEntity[] = [];

        this.plannings.forEach(planning => {
            (planning.planning_has ?? []).forEach(planningHas => {
                if (planningHas.driver_user_id !== userId) {
                    return;
                }

                if (Utils.formatDate(planningHas.begindate) !== dayKey) {
                    return;
                }

                result.push(planningHas);
            });
        });

        return result;
    }

    /** Collects all user planning entries for one user on one day for the detail dialog. */
    private getUserPlanningsForDay(userId: number, dayKey: string) {
        return this.userPlannings.filter(
            userPlanning =>
                userPlanning.user_id === userId &&
                Utils.formatDate(userPlanning.begindate) === dayKey &&
                !userPlanning.deleted_at
        );
    }

    /** Adds pointer listeners that apply row and column hover classes directly on the DOM. */
    private setupPlanningPointerOverListener() {
        const grid = this.planningGridRef?.nativeElement;
        if (!grid) {
            return;
        }

        this.cleanupPlanningPointerOver?.();

        const dayCellSelector = '.day-cell[data-user-id][data-day-key]';

        const handlePointerOver = (event: PointerEvent) => {
            const target = (event.target as HTMLElement | null)?.closest(dayCellSelector) as HTMLElement | null;
            if (!target) {
                return;
            }

            const userId = target.getAttribute('data-user-id');
            const dayKey = target.getAttribute('data-day-key');
            if (!userId || !dayKey) {
                this.clearHighlightedCells();
                return;
            }

            const key = `${userId}-${dayKey}`;
            if (this.highlightedKey === key) {
                return;
            }

            this.clearHighlightedCells();
            this.highlightedKey = key;

            const rowCells = Array.from(
                grid.querySelectorAll<HTMLElement>(
                    `.day-cell[data-user-id="${userId}"], .name-cell[data-user-id="${userId}"]`
                )
            );
            const columnCells = Array.from(grid.querySelectorAll<HTMLElement>(`.day-cell[data-day-key="${dayKey}"]`));
            const columnHeaders = Array.from(
                grid.querySelectorAll<HTMLElement>(`.day-header[data-day-key="${dayKey}"]`)
            );

            rowCells.forEach(cell => cell.classList.add('row-hover'));
            columnCells.forEach(cell => cell.classList.add('column-hover'));
            columnHeaders.forEach(cell => cell.classList.add('day-header-hover'));

            this.highlightedCells = [...new Set([...rowCells, ...columnCells, ...columnHeaders])];
        };

        const handlePointerLeave = () => {
            this.clearHighlightedCells();
        };

        this.ngZone.runOutsideAngular(() => {
            grid.addEventListener('pointerover', handlePointerOver, { passive: true });
            grid.addEventListener('pointerleave', handlePointerLeave, { passive: true });
        });

        this.cleanupPlanningPointerOver = () => {
            grid.removeEventListener('pointerover', handlePointerOver);
            grid.removeEventListener('pointerleave', handlePointerLeave);
        };
    }

    /** Removes all hover classes that were added by the pointer listeners. */
    private clearHighlightedCells() {
        this.highlightedCells.forEach(cell => {
            cell.classList.remove('row-hover', 'column-hover', 'day-header-hover');
        });

        this.highlightedCells = [];
        this.highlightedKey = undefined;
    }
}
